import React, { useEffect, useMemo, useState } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { LuSettings2, LuRectangleVertical, LuRectangleHorizontal } from 'react-icons/lu';
import { HiOutlineDocumentArrowDown } from 'react-icons/hi2';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import '../../styles/PrintableLayout.css';
import '../../styles/PrintableLayoutImposition.css';
import '../../styles/PrintableLayoutTooltips.css';

const SIZE_LABELS = {
  '5r': '5R  (5" × 7")',
  a5: 'A5  (148 × 210 mm)',
  a4: 'A4  (210 × 297 mm)',
  '4x6': '4×6"',
  '5x5': '5×5" Square',
};

const ORIENT_ICONS = {
  portrait: <><LuRectangleVertical /> Portrait</>,
  landscape: <><LuRectangleHorizontal /> Landscape</>,
};

const LAYOUT_LABELS = {
  single: 'Single Page',
  bifold: 'Bi-fold · Portrait Panels',
  cut: 'Cut · 2 Landscape Halves',
};

const PAGE_DIMS = {
  '5r': [127, 178],
  a5: [148, 210],
  a4: [210, 297],
  '4x6': [102, 152],
  '5x5': [127, 127],
};

const SIZE_SHORT = {
  '5r': '5R',
  a5: 'A5',
  a4: 'A4',
  '4x6': '4x6',
  '5x5': '5x5',
};

const ALL_SIZE_KEYS = Object.keys(SIZE_LABELS);
const ALL_LAYOUT_KEYS = Object.keys(LAYOUT_LABELS);
const IMPOSED_SIZES = new Set(['a4', 'a5']);

/*
 * Invitation-specific print restrictions.
 * Buttons remain visible so clients can see the other available formats,
 * while unsupported choices stay disabled.
 */
const PRINTABLE_PRESETS = {
  'sumaira-emiliano-nikkah-invitation': {
    defaultSize: '5x5',
    enabledSizes: ['5x5'],
    defaultOrientation: 'landscape',
    defaultLayout: 'single',
    enabledLayouts: ['single'],
  },
  'ehlyianha-faith-gil-debut-invitation': {
    defaultSize: 'a4',
    enabledSizes: ['a4'],
    defaultOrientation: 'portrait',
    defaultLayout: 'cut',
    enabledLayouts: ['cut'],
  },
};

const MM_TO_PX = 3.7795;
const PAGES_PAD = 48;
const PAGE_GAP = 24;

function waitForImage(image) {
  if (image.complete) {
    if (typeof image.decode === 'function') return image.decode().catch(() => undefined);
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    const finish = () => {
      image.removeEventListener('load', finish);
      image.removeEventListener('error', finish);
      resolve();
    };
    image.addEventListener('load', finish, { once: true });
    image.addEventListener('error', finish, { once: true });
  });
}

async function waitForRenderableAssets(root) {
  try {
    if (document.fonts?.ready) await document.fonts.ready;
  } catch {
    // Continue with the fonts already available in the browser.
  }

  const images = Array.from(root?.querySelectorAll('img') || []);
  await Promise.all(images.map(waitForImage));

  await new Promise((resolve) => {
    window.requestAnimationFrame(() => window.requestAnimationFrame(resolve));
  });
}

function layoutList(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  return String(value).split(/[\s,]+/).filter(Boolean);
}

function pageIsVisibleForLayout(child, activeLayout) {
  if (!React.isValidElement(child)) return true;

  const only = layoutList(child.props['data-layout-only']);
  const exclude = layoutList(child.props['data-layout-exclude']);

  if (only.length > 0 && !only.includes(activeLayout)) return false;
  if (exclude.includes(activeLayout)) return false;
  return true;
}

function cloneWithPageNumber(child, pageNumber) {
  if (!React.isValidElement(child)) return child;

  const originalChildren = React.Children.toArray(child.props.children);
  return React.cloneElement(
    child,
    { 'data-print-page': pageNumber },
    ...originalChildren,
    <span key="page-number" className="pl-page-num" aria-hidden="true">{pageNumber}</span>,
  );
}

function BlankPanel({ orientation }) {
  return (
    <div
      className="pl-page pl-page--blank"
      aria-label="Blank print panel"
      data-panel-orientation={orientation}
    />
  );
}

function OptionTooltip({ disabled, tooltip, children }) {
  return (
    <span
      className={`pl-option-tooltip${disabled ? ' pl-option-tooltip--disabled' : ''}`}
      data-tooltip={disabled ? tooltip : undefined}
      tabIndex={disabled ? 0 : undefined}
      aria-label={disabled ? tooltip : undefined}
    >
      {children}
    </span>
  );
}

export default function PrintableLayout({
  title,
  filename,
  supportsBifold = false,
  children,
  enabledSizes: enabledSizesProp,
  defaultSize: defaultSizeProp,
  defaultOrientation: defaultOrientationProp,
  enabledLayouts: enabledLayoutsProp,
  defaultLayout: defaultLayoutProp,
}) {
  const preset = PRINTABLE_PRESETS[filename] || {};

  const enabledSizes = useMemo(() => {
    const requested = enabledSizesProp || preset.enabledSizes || ALL_SIZE_KEYS;
    const valid = requested.filter((key) => PAGE_DIMS[key]);
    return valid.length ? valid : ALL_SIZE_KEYS;
  }, [enabledSizesProp, preset.enabledSizes]);

  const enabledLayouts = useMemo(() => {
    const requested = enabledLayoutsProp || preset.enabledLayouts || ALL_LAYOUT_KEYS;
    const valid = requested.filter((key) => ALL_LAYOUT_KEYS.includes(key));
    return valid.length ? valid : ALL_LAYOUT_KEYS;
  }, [enabledLayoutsProp, preset.enabledLayouts]);

  const initialSizeCandidate = defaultSizeProp || preset.defaultSize || '5r';
  const initialSize = enabledSizes.includes(initialSizeCandidate)
    ? initialSizeCandidate
    : enabledSizes[0];

  const initialOrientation = defaultOrientationProp
    || preset.defaultOrientation
    || 'landscape';

  const initialLayoutCandidate = defaultLayoutProp || preset.defaultLayout || 'single';
  const initialLayoutAllowed = enabledLayouts.includes(initialLayoutCandidate)
    && (
      initialLayoutCandidate === 'single'
      || (supportsBifold && IMPOSED_SIZES.has(initialSize))
    );
  const initialLayout = initialLayoutAllowed
    ? initialLayoutCandidate
    : enabledLayouts.find((key) => (
      key === 'single' || (supportsBifold && IMPOSED_SIZES.has(initialSize))
    )) || 'single';

  const [size, setSize] = useState(initialSize);
  const [orient, setOrient] = useState(initialOrientation);
  const [layout, setLayout] = useState(initialLayout);
  const [saving, setSaving] = useState(false);
  const [savedFilename, setSavedFilename] = useState('');
  const [showOptions, setShowOptions] = useState(false);
  const [previewScale, setPreviewScale] = useState(1);

  const [pageWidth, pageHeight] = PAGE_DIMS[size];
  const isSquareSize = pageWidth === pageHeight;
  const hasRestrictedSizes = enabledSizes.length < ALL_SIZE_KEYS.length;
  const hasRestrictedLayouts = supportsBifold && enabledLayouts.length < ALL_LAYOUT_KEYS.length;
  const imposedAvailable = supportsBifold && IMPOSED_SIZES.has(size);
  const isBifold = imposedAvailable && layout === 'bifold';
  const isCut = imposedAvailable && layout === 'cut';
  const isImposed = isBifold || isCut;
  const activeLayout = isBifold ? 'bifold' : isCut ? 'cut' : 'single';

  const effectiveOrient = isBifold ? 'landscape' : isCut ? 'portrait' : orient;
  const panelOrientation = isBifold ? 'portrait' : isCut ? 'landscape' : effectiveOrient;

  const layoutIsSelectable = (key) => (
    enabledLayouts.includes(key)
    && (key === 'single' || imposedAvailable)
  );

  useEffect(() => {
    if (layoutIsSelectable(layout)) return;

    const fallback = enabledLayouts.find((key) => (
      key === 'single' || imposedAvailable
    ));
    setLayout(fallback || 'single');
  // layoutIsSelectable is derived from the values listed below.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabledLayouts, imposedAvailable, layout]);

  useEffect(() => {
    if (isBifold && orient !== 'landscape') setOrient('landscape');
    if (isCut && orient !== 'portrait') setOrient('portrait');
  }, [isBifold, isCut, orient]);

  const numberedPages = useMemo(() => {
    const visiblePages = React.Children.toArray(children)
      .filter((child) => pageIsVisibleForLayout(child, activeLayout));

    return visiblePages.map((child, index) => cloneWithPageNumber(child, index + 1));
  }, [activeLayout, children]);

  const sheets = useMemo(() => {
    if (!isImposed) {
      return numberedPages.map((page, index) => (
        <div
          className="pl-sheet pl-sheet--single"
          key={`sheet-${index + 1}`}
          data-panel-orientation={panelOrientation}
        >
          {page}
        </div>
      ));
    }

    const pairedSheets = [];
    for (let index = 0; index < numberedPages.length; index += 2) {
      const firstPage = numberedPages[index];
      const secondPage = numberedPages[index + 1] || <BlankPanel orientation={panelOrientation} />;

      if (isBifold) {
        pairedSheets.push(
          <div
            className="pl-sheet pl-sheet--bifold"
            key={`bifold-sheet-${index / 2 + 1}`}
            data-panel-orientation="portrait"
          >
            <div className="pl-fold-panel pl-fold-panel--left pl-panel-orient-portrait">{firstPage}</div>
            <div className="pl-fold-panel pl-fold-panel--right pl-panel-orient-portrait">{secondPage}</div>
            <div className="pl-fold-guide" aria-hidden="true">
              <span>FOLD</span>
              <span>FOLD</span>
            </div>
          </div>,
        );
      } else {
        pairedSheets.push(
          <div
            className="pl-sheet pl-sheet--cut"
            key={`cut-sheet-${index / 2 + 1}`}
            data-panel-orientation="landscape"
          >
            <div className="pl-cut-panel pl-cut-panel--top pl-panel-orient-landscape">{firstPage}</div>
            <div className="pl-cut-panel pl-cut-panel--bottom pl-panel-orient-landscape">{secondPage}</div>
            <div className="pl-cut-guide" aria-hidden="true">
              <span>CUT</span>
              <span>CUT</span>
            </div>
          </div>,
        );
      }
    }

    return pairedSheets;
  }, [isBifold, isImposed, numberedPages, panelOrientation]);

  const [sheetW, sheetH] = useMemo(() => {
    const [portraitW, portraitH] = PAGE_DIMS[size];
    if (effectiveOrient === 'landscape' && portraitW !== portraitH) return [portraitH, portraitW];
    return [portraitW, portraitH];
  }, [effectiveOrient, size]);

  useEffect(() => {
    const update = () => {
      const sheetWidthPx = sheetW * MM_TO_PX;
      const available = Math.max(280, window.innerWidth - 32);
      setPreviewScale(available < sheetWidthPx ? Math.max(0.22, available / sheetWidthPx) : 1);
    };

    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [sheetW]);

  const handleSizeChange = (nextSize) => {
    if (!enabledSizes.includes(nextSize)) return;
    setSize(nextSize);

    if (!IMPOSED_SIZES.has(nextSize) && layout !== 'single') {
      setLayout(enabledLayouts.includes('single') ? 'single' : enabledLayouts[0]);
    }
  };

  const handleLayoutChange = (nextLayout) => {
    if (!layoutIsSelectable(nextLayout)) return;
    setLayout(nextLayout);

    if (nextLayout === 'bifold') setOrient('landscape');
    if (nextLayout === 'cut') setOrient('portrait');
  };

  const handleOrientationChange = (nextOrientation) => {
    if (isSquareSize || isImposed) return;
    setOrient(nextOrientation);
  };

  const handleSavePDF = async () => {
    setSaving(true);

    const pagesEl = document.querySelector('.pl-pages');
    const wrapperEl = pagesEl?.closest('.pl-wrapper');
    const savedTransform = pagesEl?.style.transform ?? '';
    const savedMarginBottom = pagesEl?.style.marginBottom ?? '';

    if (pagesEl) {
      pagesEl.style.transform = 'none';
      pagesEl.style.marginBottom = '';
    }
    wrapperEl?.classList.add('pl-capturing', 'pl-pdf-exporting');

    try {
      await waitForRenderableAssets(pagesEl || document.body);

      const pdf = new jsPDF({
        orientation: effectiveOrient,
        unit: 'mm',
        format: [sheetW, sheetH],
        compress: true,
        precision: 16,
      });

      const sheetElements = document.querySelectorAll('.pl-sheet');
      for (let index = 0; index < sheetElements.length; index += 1) {
        const sheet = sheetElements[index];
        const canvas = await html2canvas(sheet, {
          scale: 3,
          useCORS: true,
          allowTaint: false,
          backgroundColor: null,
          logging: false,
          imageTimeout: 15000,
          removeContainer: true,
          windowWidth: Math.max(document.documentElement.clientWidth, Math.ceil(sheet.scrollWidth)),
          windowHeight: Math.max(document.documentElement.clientHeight, Math.ceil(sheet.scrollHeight)),
          onclone: (clonedDocument) => {
            clonedDocument.querySelector('.pl-wrapper')?.classList.add('pl-capturing', 'pl-pdf-exporting');
            const clonedPages = clonedDocument.querySelector('.pl-pages');
            if (clonedPages) {
              clonedPages.style.transform = 'none';
              clonedPages.style.marginBottom = '';
            }
          },
        });

        const imageData = canvas.toDataURL('image/png');
        if (index > 0) pdf.addPage([sheetW, sheetH], effectiveOrient);
        pdf.addImage(imageData, 'PNG', 0, 0, sheetW, sheetH, undefined, 'FAST');
      }

      const orientationSuffix = isSquareSize && !isImposed ? 'square' : effectiveOrient;
      const layoutSuffix = isBifold ? '-bifold' : isCut ? '-cut-2up' : '';
      const outputName = `${filename}-${SIZE_SHORT[size]}-${orientationSuffix}${layoutSuffix}.pdf`;
      pdf.save(outputName);
      setSavedFilename(outputName);
    } finally {
      wrapperEl?.classList.remove('pl-capturing', 'pl-pdf-exporting');
      if (pagesEl) {
        pagesEl.style.transform = savedTransform;
        pagesEl.style.marginBottom = savedMarginBottom;
      }
      setSaving(false);
    }
  };

  const naturalHeight = sheets.length * (sheetH * MM_TO_PX)
    + Math.max(0, sheets.length - 1) * PAGE_GAP
    + PAGES_PAD;
  const pagesStyle = previewScale < 1
    ? {
        transform: `scale(${previewScale})`,
        transformOrigin: 'top center',
        marginBottom: `${naturalHeight * (previewScale - 1)}px`,
      }
    : undefined;

  const layoutMeta = isBifold
    ? `landscape sheet · portrait fold panels · ${sheets.length} sheets`
    : isCut
      ? `portrait sheet · landscape cut panels · ${sheets.length} sheets`
      : isSquareSize
        ? `square · ${numberedPages.length} pages`
        : `${effectiveOrient} · ${numberedPages.length} pages`;

  const wrapperClassName = [
    'pl-wrapper',
    `pl-size-${size}`,
    `pl-orient-${effectiveOrient}`,
    `pl-layout-${activeLayout}`,
    `pl-panel-orient-${panelOrientation}`,
  ].join(' ');

  return (
    <div className={wrapperClassName}>
      {saving && (
        <div className="pl-generating-overlay" role="dialog" aria-modal="true" aria-label="Generating PDF">
          <div className="pl-generating-box">
            <AiOutlineLoading3Quarters className="pl-generating-spinner" />
            <p className="pl-generating-title">Generating PDF...</p>
            <p className="pl-generating-sub">Please wait while every sheet is prepared.</p>
          </div>
        </div>
      )}

      {savedFilename && (
        <div className="pl-generating-overlay" role="dialog" aria-modal="true" aria-label="PDF saved">
          <div className="pl-generating-box">
            <div className="pl-done-icon">✓</div>
            <p className="pl-generating-title">PDF Saved!</p>
            <p className="pl-generating-sub pl-done-filename">{savedFilename}</p>
            <p className="pl-generating-sub">Your printable invitation has been downloaded.</p>
            <button type="button" className="pl-done-btn" onClick={() => setSavedFilename('')}>Done</button>
          </div>
        </div>
      )}

      {showOptions && (
        <div className="pl-modal-backdrop" onClick={() => setShowOptions(false)}>
          <div className="pl-modal" onClick={(event) => event.stopPropagation()} role="dialog" aria-modal="true" aria-label="Print options">
            <div className="pl-modal-header">
              <h2 className="pl-modal-title">Print Options</h2>
              <button type="button" className="pl-modal-close" onClick={() => setShowOptions(false)} aria-label="Close">×</button>
            </div>

            <div className="pl-modal-body">
              <div className="pl-modal-section">
                <p className="pl-modal-label">Paper Size</p>
                <div className="pl-opt-group">
                  {Object.entries(SIZE_LABELS).map(([key, label]) => {
                    const isEnabled = enabledSizes.includes(key);
                    const tooltip = `${label} is disabled for this printable. Contact support to request this paper size.`;

                    return (
                      <OptionTooltip key={key} disabled={!isEnabled} tooltip={tooltip}>
                        <button
                          type="button"
                          disabled={!isEnabled}
                          aria-disabled={!isEnabled}
                          className={`pl-opt-btn${size === key ? ' pl-opt-btn--active' : ''}${!isEnabled ? ' pl-opt-btn--disabled' : ''}`}
                          onClick={() => handleSizeChange(key)}
                        >
                          {label}
                        </button>
                      </OptionTooltip>
                    );
                  })}
                </div>
                {hasRestrictedSizes && (
                  <p className="pl-option-note">
                    Only the enabled size is available for this printable. <strong>Contact support</strong> to request another paper size.
                  </p>
                )}
              </div>

              {supportsBifold && (
                <div className="pl-modal-section">
                  <p className="pl-modal-label">Sheet Layout</p>
                  <div className="pl-opt-group">
                    {ALL_LAYOUT_KEYS.map((key) => {
                      const disabledByPreset = !enabledLayouts.includes(key);
                      const disabledBySize = key !== 'single' && !imposedAvailable;
                      const disabled = disabledByPreset || disabledBySize;
                      const tooltip = disabledByPreset
                        ? `${LAYOUT_LABELS[key]} is disabled for this printable.`
                        : 'Choose A4 or A5 to enable this sheet layout.';

                      return (
                        <OptionTooltip key={key} disabled={disabled} tooltip={tooltip}>
                          <button
                            type="button"
                            disabled={disabled}
                            aria-disabled={disabled}
                            className={`pl-opt-btn${activeLayout === key ? ' pl-opt-btn--active' : ''}${disabled ? ' pl-opt-btn--disabled' : ''}`}
                            onClick={() => handleLayoutChange(key)}
                          >
                            {LAYOUT_LABELS[key]}
                          </button>
                        </OptionTooltip>
                      );
                    })}
                  </div>
                  {hasRestrictedLayouts && (
                    <p className="pl-option-note">
                      Only the enabled sheet layout is available for this printable.
                    </p>
                  )}
                  <p className="pl-modal-help">
                    {!imposedAvailable && 'Choose A4 or A5 to enable bi-fold and cut layouts.'}
                    {isBifold && 'Bi-fold uses one landscape sheet with two portrait invitation panels and a vertical center fold guide.'}
                    {isCut && 'Cut uses one portrait sheet with two landscape half-sheet invitations and a horizontal center cut guide.'}
                    {imposedAvailable && activeLayout === 'single' && 'Choose bi-fold for portrait panels or cut for two landscape half-sheet invitations.'}
                  </p>
                </div>
              )}

              <div className="pl-modal-section">
                <p className="pl-modal-label">Orientation</p>
                <div className="pl-opt-group">
                  {Object.entries(ORIENT_ICONS).map(([key, label]) => {
                    const disabled = isSquareSize || isImposed;
                    const tooltip = isImposed
                      ? 'Orientation is controlled by the selected sheet layout.'
                      : 'Orientation is disabled because this paper size is square.';

                    return (
                      <OptionTooltip key={key} disabled={disabled} tooltip={tooltip}>
                        <button
                          type="button"
                          disabled={disabled}
                          aria-disabled={disabled}
                          className={`pl-opt-btn${effectiveOrient === key ? ' pl-opt-btn--active' : ''}${disabled ? ' pl-opt-btn--disabled' : ''}`}
                          onClick={() => handleOrientationChange(key)}
                        >
                          {label}
                        </button>
                      </OptionTooltip>
                    );
                  })}
                </div>
                {isSquareSize && !isImposed && (
                  <p className="pl-option-note">
                    Orientation is locked because a square page has equal width and height.
                  </p>
                )}
                {isBifold && <p className="pl-modal-help">Sheet: landscape. Each fold panel: portrait.</p>}
                {isCut && <p className="pl-modal-help">Sheet: portrait. Each half after cutting: landscape.</p>}
              </div>
            </div>

            <div className="pl-modal-footer">
              <button type="button" className="pl-modal-done" onClick={() => setShowOptions(false)}>Done</button>
            </div>
          </div>
        </div>
      )}

      <div className="pl-topbar">
        <div className="pl-topbar-copy">
          <h1 className="pl-topbar-title">{title}</h1>
          <span className="pl-topbar-meta">{SIZE_SHORT[size]} · {layoutMeta}</span>
        </div>
        <div className="pl-topbar-actions">
          <button type="button" className="pl-btn-options" onClick={() => setShowOptions(true)} disabled={saving}>
            <LuSettings2 /> Options
          </button>
          <button type="button" className="pl-btn-print" onClick={handleSavePDF} disabled={saving}>
            <HiOutlineDocumentArrowDown /> Save as PDF
          </button>
        </div>
      </div>

      <div
        className="pl-pages"
        style={pagesStyle}
        data-sheet-width-mm={sheetW}
        data-sheet-height-mm={sheetH}
        data-panel-orientation={panelOrientation}
      >
        {sheets}
      </div>
    </div>
  );
}
